import {
  Chip,
  TextField,
  Autocomplete,
  Checkbox,
  Box,
  FormHelperText,
  Typography,
} from "@mui/material";
import * as _ from "lodash";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { useEffect, useState } from "react";
import { FormHelperTextError } from "../errors";
import { AnimatePresence, motion } from "framer-motion";
import { APP_COLORS } from "../../../enums";
import FlexBox from "../../layouts/Flexbox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function ChipTextField({
  limitTags,
  autoCompleteSize = "small",
  chipSize = "small",
  textFieldVariant = "standard",
  inlineLabel = false,
  textfieldStyle = {},
  invalidChips = [],
  options = [],
  freeSolo = false,
  multiple = true,
  errorProps,
  inputProps,
  labelProps,
  renderTags = true,
  rootProps = {},
  chipTextFieldListConfig,
  helperText,
}: any) {
  const isFlatList = typeof options?.[0] === "object" ? false : true;
  chipTextFieldListConfig = {
    labelField: "label",
    valueField: "id",
    selectedValueField: "label",
    ...chipTextFieldListConfig,
  };
  const { labelField, valueField } = chipTextFieldListConfig;

  const updatedValue = inputProps?.value
    ? Array.isArray(inputProps?.value)
      ? inputProps?.value
      : String(inputProps?.value)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  const [value, setValue] = useState();
  const [draftInputValue, setDraftInputValue] = useState("");

  useEffect(() => {
    if (!_.isEqual(inputProps?.value, value)) {
      inputProps?.onChange({
        target: {
          name: inputProps?.name,
          value: _.uniq(updatedValue),
        },
      });
      setValue(inputProps?.value);
    }
  }, [inputProps?.value]);

  const onValueChange = (e, inputValue) => {
    setDraftInputValue("");

    if (typeof inputValue?.[inputValue?.length - 1] === "object") {
      const newValue = inputValue?.[inputValue?.length - 1][valueField];
      inputValue?.pop();

      inputProps?.onChange({
        target: {
          name: inputProps?.name,
          value: _.uniq([...inputValue, newValue]),
        },
      });
    } else {
      inputProps?.onChange({
        target: {
          name: inputProps?.name,
          value: _.uniq(inputValue),
        },
      });
    }
  };

  const showErrorMessage =
    (errorProps?.error || errorProps?.helperText) &&
    errorProps?.showError !== false;
  const resolvedHelperText = errorProps?.error
    ? undefined
    : helperText ?? inputProps?.helperText;
  const errorMessage = showErrorMessage
    ? errorProps?.error || errorProps?.helperText
    : undefined;
  const leftHelperContent = errorMessage ?? resolvedHelperText;
  const showEnterHint = freeSolo && draftInputValue.trim().length > 0;
  const showHelperRow = leftHelperContent || showEnterHint;

  return (
    <Autocomplete
      multiple={multiple}
      id={inputProps?.id}
      getOptionLabel={(option) => {
        if (isFlatList) {
          return typeof option === "string" ? option : String(option);
        }
        return option?.[labelField] || "";
      }}
      size={autoCompleteSize}
      limitTags={limitTags}
      options={options || []}
      value={updatedValue}
      autoHighlight
      isOptionEqualToValue={(option, value) => {
        if (isFlatList) {
          return option === value;
        }
        return option?.[valueField] === value;
      }}
      onChange={onValueChange}
      freeSolo={freeSolo}
      disableCloseOnSelect
      renderTags={(inputValue, getTagProps) => {
        if (!renderTags) return null;
        return inputValue.map((option, index) => {
          return (
            <Chip
              size={chipSize}
              key={index}
              variant="outlined"
              label={
                isFlatList
                  ? option
                  : options?.find((o) => o?.[valueField] === option)?.[
                  labelField
                  ] || option
              }
              {...getTagProps({ index })}
              sx={{
                "& .MuiChip-deleteIcon": {
                  color: APP_COLORS.primaryDark,
                },
                ...(invalidChips?.length && invalidChips?.includes(index)
                  ? {
                    color: APP_COLORS.errorDark,
                    borderColor: APP_COLORS.error,
                    backgroundColor: APP_COLORS.errorLight,
                  }
                  : {
                    color: APP_COLORS.primaryDark,
                    borderColor: APP_COLORS.primaryLight3,
                    backgroundColor: APP_COLORS.primaryLight,
                  }),
              }}
            />
          );
        });
      }}
      renderOption={(props, option, { selected }) => {
        const isSelected = updatedValue?.includes(
          isFlatList ? option : option?.[valueField]
        );
        return (
          <li
            {...props}
            key={`chiptextfield-listitem-${option?.[valueField]}-${props?.["data-option-index"]}`}
          >
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={isSelected}
            />
            {isFlatList ? option : option?.[labelField]}
          </li>
        );
      }}
      renderInput={(params) => {
        const textInputProps: any = params.inputProps;
        textInputProps.autoComplete = "off";
        textInputProps.style = { ...textfieldStyle };

        if (freeSolo) {
          const handleInputChange = textInputProps.onChange;
          textInputProps.onChange = (event) => {
            setDraftInputValue(event.target.value);
            handleInputChange?.(event);
          };
        }

        return (
          <Box>
            <TextField
              sx={{
                "& .MuiInputBase-root": {
                  height: "auto",
                },
              }}
              error={errorProps?.error ? true : false}
              {...params}
              id={inputProps?.id}
              variant={textFieldVariant}
              name={inputProps?.name}
              placeholder={
                options && options?.length ? "" : "Type and press enter."
              }
              label={inlineLabel ? labelProps?.label : ""}
              autoComplete="off"
              multiline={textFieldVariant === "outlined"}
              {...inputProps}
              helperText={undefined}
              onChange={() => { }}
            />
            {showHelperRow && (
              <FlexBox
                width="100%"
                alignItems="center"
                justifyContent={
                  leftHelperContent ? "space-between" : "flex-end"
                }
                mt={0.375}
                px={textFieldVariant === "outlined" ? 1.75 : 0}
              >
                {leftHelperContent &&
                  (errorMessage ? (
                    <FormHelperTextError
                      id={`component-helper-text-${inputProps?.id}`}
                      error={errorProps?.error}
                      helperText={errorProps?.helperText}
                      showError={true}
                      sx={{ m: 0, flex: 1, height: "auto" }}
                    />
                  ) : (
                    <FormHelperText sx={{ m: 0, flex: 1 }}>
                      {leftHelperContent}
                    </FormHelperText>
                  ))}
                {showEnterHint && (
                  <FlexBox alignItems="center" gap={0.5} flexShrink={0}>
                    <Typography
                      component="span"
                      variant="caption"
                      color="textSecondary"
                    >
                      Press Enter
                    </Typography>
                    <KeyboardReturnIcon
                      color="primary"
                      fontSize="small"
                      titleAccess="Press Enter"
                    />
                  </FlexBox>
                )}
              </FlexBox>
            )}
          </Box>
        );
      }}
    />
  );
}

export { ChipTextField };
